import { useState } from 'react';
import type { ReactNode } from 'react';
import { AuthError } from '../auth/provider';
import type { AuthProvider } from '../auth/provider';

/**
 * Sign in.
 *
 * The dev banner is not decoration, and it matters more here than on the staff
 * app. `VITE_AUTH_FAKE` mints unsigned tokens, and `admin-api`'s `FakeVerifier`
 * refuses to exist under `NODE_ENV=production` with a sentence that says why:
 * an unsigned operator token is not one tenant's book, it is every tenant's. A
 * screen that looked identical in both modes would undo that on this side of
 * the wire.
 *
 * There is no "create account" and there will not be one. The operator claim is
 * set by an operator provisioning another operator; a staff token from a real
 * tenant is refused at the door, and so is a token that carries both claims.
 */
export function SignIn({ auth }: { auth: AuthProvider }): ReactNode {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await auth.signIn(email, password);
    } catch (e) {
      setError(
        e instanceof AuthError && e.reason === 'unavailable'
          ? 'Could not reach the sign-in service. Check the connection.'
          : 'That email and password did not match.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <form
        className="signin__panel"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <h1>yourstore operator</h1>
        <p className="signin__sub">
          The cross-tenant seat. Your token must carry <code>operator: true</code> and no{' '}
          <code>tenant_id</code>.
        </p>

        {auth.kind === 'fake' ? (
          <p className="signin__dev">
            Development sign-in. Any email works and the token is unsigned — this build talks only
            to an <code>admin-api</code> started with <code>AUTH_FAKE_TOKENS=1</code>, which refuses
            to run in production.
          </p>
        ) : null}

        <label className="field">
          <span className="field__label">Email</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        {auth.kind === 'firebase' ? (
          <label className="field">
            <span className="field__label">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
        ) : null}

        {error !== null ? <p className="failure__message">{error}</p> : null}

        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </main>
  );
}
